import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';

import {
  getIsSessionCookieModeEnabled,
  SESSION_REQUEST_HEADER_NAME,
  SESSION_REQUEST_HEADER_VALUE,
} from '../sessionCookieMode';

import { prepareTermSuggestionPayload } from './adapters/prepareTermSuggestionPayload';
import { getReversTranslateConfiguration } from './dynamicTermSuggestion.configuration';
import {
  TERM_SUGGESTION_SEND_STATUS,
  TermSuggestionSendResult,
} from './dynamicTermSuggestion.types';
import {
  applyDynamicTermVerdicts,
  discardDynamicTerms,
} from './dynamicTermSuggestionStore';
import {
  getReversTranslateToken,
  invalidateReversTranslateToken,
} from './getReversTranslateToken';

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
} as const;

const BFF_REVERS_TRANSLATE_PATH = '/api/revers-translate/';

const RETRYABLE: TermSuggestionSendResult = {
  status: TERM_SUGGESTION_SEND_STATUS.RETRYABLE,
};
const ACKNOWLEDGED: TermSuggestionSendResult = {
  status: TERM_SUGGESTION_SEND_STATUS.ACKNOWLEDGED,
};
const CIRCUIT_BROKEN: TermSuggestionSendResult = {
  status: TERM_SUGGESTION_SEND_STATUS.CIRCUIT_BROKEN,
};

const sendBatch = async ({
  keys,
  origin,
  shouldKeepAlive,
}: {
  keys: string[];
  origin: string;
  shouldKeepAlive: boolean;
}): Promise<Response | undefined> => {
  const configuration = getReversTranslateConfiguration();

  if (!configuration) {
    return undefined;
  }

  const intakePath = `projects/${configuration.projectId}/suggestions/terms`;
  const body = JSON.stringify(prepareTermSuggestionPayload({ keys, origin }));

  if (getIsSessionCookieModeEnabled()) {
    return fetch(`${BFF_REVERS_TRANSLATE_PATH}${intakePath}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_REQUEST_HEADER_NAME]: SESSION_REQUEST_HEADER_VALUE,
      },
      body,
      keepalive: shouldKeepAlive,
    });
  }

  const token = await getReversTranslateToken();

  if (!token) {
    return undefined;
  }

  return fetch(`${configuration.baseUrl}api/${intakePath}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.accessToken}`,
      'X-Id-Token': token.idToken,
    },
    body,
    keepalive: shouldKeepAlive,
  });
};

export const postTermSuggestions = async ({
  keys,
  origin,
  shouldKeepAlive = false,
  shouldRetryOnUnauthorized = true,
}: {
  keys: string[];
  origin: string;
  shouldKeepAlive?: boolean;
  shouldRetryOnUnauthorized?: boolean;
}): Promise<TermSuggestionSendResult> => {
  try {
    const response = await sendBatch({ keys, origin, shouldKeepAlive });

    if (!response) {
      return RETRYABLE;
    }

    if (response.ok) {
      const { results } = await response.json();

      applyDynamicTermVerdicts({ keys, results: results ?? [] });

      return ACKNOWLEDGED;
    }

    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      invalidateReversTranslateToken();

      if (!shouldRetryOnUnauthorized) {
        return RETRYABLE;
      }

      return postTermSuggestions({
        keys,
        origin,
        shouldKeepAlive,
        shouldRetryOnUnauthorized: false,
      });
    }

    if (
      response.status === HTTP_STATUS.FORBIDDEN ||
      response.status === HTTP_STATUS.NOT_FOUND
    ) {
      logError(new Error('Revers Translate term suggestion intake refused'), {
        operation: 'Post term suggestions',
        status: response.status,
      });

      return CIRCUIT_BROKEN;
    }

    if (response.status === HTTP_STATUS.BAD_REQUEST) {
      logError(new Error('Revers Translate term suggestion payload refused'), {
        operation: 'Post term suggestions',
        status: response.status,
      });

      discardDynamicTerms({ keys });

      return ACKNOWLEDGED;
    }

    return RETRYABLE;
  } catch (error) {
    logError(error, {
      operation: 'Post term suggestions',
    });

    return RETRYABLE;
  }
};
